import { inject, Injectable } from '@angular/core';
import {
  getEmptyFreightBasicForCarrier,
  getEmptyFreightDetailedForCarrier,
  IFreightBasicForCarrier,
  IFreightDetailedForCarrier,
} from '@okcargo-apps/shared-domain';
import {
  FindFreightForCarrierQueryHandler,
  FreightBasicForCarrierResponse,
  SearchFinishedFreightsForCarriersQueryHandler,
  SearchInProgressFreightsForCarriersQueryHandler,
} from '@okcargo/query-processor';
import {
  SearchOptionsInput,
  transformToSearchOptions,
  transformToSearchResult,
} from '../query-repository/query.mapper';
import { FindOptions, SearchResult } from '../query-repository/query.model';
import { BaseQueryRepository } from '../query-repository/query.repository';
import { transformToIFreightBasicForCarrier, transformToIFreightDetailForCarrier } from './freight-carrier-mapper';

@Injectable({
  providedIn: 'root',
})
export class FreightCarrierQueryService {
  private readonly queryRepository = inject(BaseQueryRepository);

  private readonly searchInProgressFreightsQueryHandler: SearchInProgressFreightsForCarriersQueryHandler;
  private readonly searchFinishedFreightsQueryHandler: SearchFinishedFreightsForCarriersQueryHandler;
  private readonly findFreightQueryHandler: FindFreightForCarrierQueryHandler;

  constructor() {
    const queryRepository = this.queryRepository;

    this.searchInProgressFreightsQueryHandler = new SearchInProgressFreightsForCarriersQueryHandler(queryRepository);
    this.searchFinishedFreightsQueryHandler = new SearchFinishedFreightsForCarriersQueryHandler(queryRepository);
    this.findFreightQueryHandler = new FindFreightForCarrierQueryHandler(queryRepository);
  }

  public async searchInProgressFreights(
    options: SearchOptionsInput = {},
  ): Promise<SearchResult<IFreightBasicForCarrier>> {
    // SearchInProgressFreightsForCarriersQuery
    const response = await this.searchInProgressFreightsQueryHandler.handle(transformToSearchOptions(options));

    return this.toDomainResult(response);
  }

  public async searchFinishedFreights(
    options: SearchOptionsInput = {},
  ): Promise<SearchResult<IFreightBasicForCarrier>> {
    // SearchFinishedFreightsForCarriersQuery
    const response = await this.searchFinishedFreightsQueryHandler.handle(transformToSearchOptions(options));

    return this.toDomainResult(response);
  }

  public async findFreight(options: FindOptions): Promise<IFreightDetailedForCarrier> {
    // FindFreightForCarrierQuery
    const response = await this.findFreightQueryHandler.handle({
      id: options.id,
    });

    return transformToIFreightDetailForCarrier(response, getEmptyFreightDetailedForCarrier());
  }

  private toDomainResult(response: {
    items: FreightBasicForCarrierResponse[];
    metadata: { totalElements: number };
  }): SearchResult<IFreightBasicForCarrier> {
    return transformToSearchResult(response, (freightBasicResponse) =>
      transformToIFreightBasicForCarrier(freightBasicResponse, getEmptyFreightBasicForCarrier()),
    );
  }
}
